// SmartRevisionPage.tsx

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Avatar,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import UpdateIcon from "@mui/icons-material/Update";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { groupAndChunkQuestions, joinLocalizedString } from "@/utils/commonUtils";
import quizApi from "@/services/quizService";
import type { WrongQuestionData } from "@/types/Quiz.types";
import { renderWithLineBreaks } from "@/utils/elementUtils";
import { useAppNavigate } from "@/hooks/useAppNavigate";

export const SmartRevisionPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useAppNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [wrongQuestions, setWrongQuestions] = useState<WrongQuestionData[]>([]);

  const fetchWrongQuestion = () => {
    quizApi.getNeverCorrectQuestions().then((result) => {
      if (result.data.length === 0) {
        quizApi.getWrongQuestions().then((result) => {
          setWrongQuestions(result.data);
        });
      } else {
        setWrongQuestions(result.data);
      }
    });
  };

  useEffect(() => {
    fetchWrongQuestion();
  }, []);

  // Group by Quiz ID, then slice into chunks of 10
  const groupedData = useMemo(() => groupAndChunkQuestions(wrongQuestions, 10), [wrongQuestions]);

  // Utility to find the most recent attempt date formatted using the active locale
  const getMostRecentAttempt = (chunk: WrongQuestionData[]) => {
    const dates = chunk.map((q) => new Date(q.lastAttemptAt).getTime());
    const mostRecent = Math.max(...dates);
    return new Intl.DateTimeFormat(i18n.language || "en-HK", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(mostRecent));
  };

  // Utility to find the highest wrong count in a chunk
  const getMaxWrongCount = (chunk: WrongQuestionData[]) => {
    return Math.max(...chunk.map((q) => q.wrongCount));
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary", py: 6 }}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box sx={{ mb: 6, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h4" component="h1" sx={{ color: "primary.main", fontWeight: 800 }}>
            {t("pages.smartRevision.title")}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
            {t("pages.smartRevision.descriptionPrefix")}
            <Box component="span" sx={{ fontWeight: "bold", color: "text.primary" }}>
              {wrongQuestions.length}
            </Box>
            {t("pages.smartRevision.descriptionSuffix")}
          </Typography>
        </Box>

        {/* Dashboard-style Wide Cards using CSS Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "repeat(2, 1fr)" },
            gap: 4,
          }}
        >
          {groupedData.map((quizGroup) =>
            quizGroup.chunks.map((chunk, chunkIndex) => (
              <Card
                key={`${quizGroup.quizId}-chunk-${chunkIndex}`}
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.03)",
                  transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: isDark ? "0 12px 24px -10px rgba(0,0,0,0.6)" : "0 12px 24px -10px rgba(0,0,0,0.1)",
                    borderColor: "primary.main",
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* Top Header Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Stack direction="row" sx={{ gap: 2.5, alignItems: "center" }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          color: "primary.main",
                          width: 56,
                          height: 56,
                        }}
                      >
                        <AutoStoriesOutlinedIcon fontSize="medium" />
                      </Avatar>
                      <Box>
                        <Stack direction="row" sx={{ alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="overline"
                            sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 1, lineHeight: 1 }}
                          >
                            {t("pages.smartRevision.part", { index: chunkIndex + 1 })}
                          </Typography>
                          <Chip
                            label={t("pages.smartRevision.questionsCount", { count: chunk.length })}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, borderRadius: 1 }}
                          />
                        </Stack>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, color: "text.primary" }}>
                          {quizGroup.quizMeta.paper}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                          {t("pages.smartRevision.quizId", { id: quizGroup.quizMeta.name })}
                        </Typography>
                      </Box>
                    </Stack>

                    <Button
                      variant="contained"
                      color="primary"
                      disableElevation
                      endIcon={<PlayArrowRoundedIcon />}
                      sx={{
                        py: 1.2,
                        px: 3,
                        borderRadius: 1,
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                      onClick={() => {
                        navigate(`/mock/quiz/${quizGroup.quizMeta.name}/mcq`, {
                          state: {
                            quizId: quizGroup.quizMeta.id,
                            exam: quizGroup.quizMeta.name,
                            paper: quizGroup.quizMeta.paper,
                            section: quizGroup.quizMeta.section,
                            limit: chunk.length,
                            ids: chunk.map((c) => c._id),
                          },
                        });
                      }}
                    >
                      {t("pages.smartRevision.redoButton", { index: chunkIndex + 1 })}
                    </Button>
                  </Box>

                  <Divider sx={{ mb: 3, borderColor: "divider" }} />

                  {/* Two-Column Internal Layout for Information */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1.5fr" },
                      gap: 4,
                    }}
                  >
                    {/* Left Column: Quick Stats */}
                    <Stack sx={{ gap: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "text.primary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        {t("pages.smartRevision.performanceStats")}
                      </Typography>

                      <Stack direction="row" sx={{ alignItems: "center", gap: 1.5, color: "text.secondary" }}>
                        <UpdateIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t("pages.smartRevision.lastAttempt")}{" "}
                          <Box component="span" sx={{ color: "text.primary" }}>
                            {getMostRecentAttempt(chunk)}
                          </Box>
                        </Typography>
                      </Stack>

                      <Stack direction="row" sx={{ alignItems: "center", gap: 1.5, color: "text.secondary" }}>
                        <TrendingDownIcon fontSize="small" sx={{ color: "error.main" }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t("pages.smartRevision.highestFailed")}{" "}
                          <Box component="span" sx={{ color: "error.main", fontWeight: 700 }}>
                            {getMaxWrongCount(chunk)}
                          </Box>
                        </Typography>
                      </Stack>

                      <Stack direction="row" sx={{ alignItems: "center", gap: 1.5, color: "text.secondary" }}>
                        <FormatListNumberedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t("pages.smartRevision.targetSection")}{" "}
                          <Box component="span" sx={{ color: "text.primary" }}>
                            {quizGroup.quizMeta.section === -1
                              ? t("pages.smartRevision.sectionGeneral")
                              : quizGroup.quizMeta.section}
                          </Box>
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Right Column: Content Preview */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "text.primary",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          mb: 1,
                        }}
                      >
                        {t("pages.smartRevision.areasStumbledOn")}
                      </Typography>
                      <List disablePadding>
                        {chunk.slice(0, 3).map((q, idx) => (
                          <ListItem key={idx} disableGutters sx={{ alignItems: "flex-start", py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                              <CloseRoundedIcon sx={{ fontSize: 16, color: "error.main" }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={renderWithLineBreaks(joinLocalizedString(q.lastSelected?.optionText))}
                              slotProps={{
                                primary: {
                                  variant: "body2",
                                  sx: {
                                    color: "text.secondary",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  },
                                },
                              }}
                            />
                          </ListItem>
                        ))}
                        {chunk.length > 3 && (
                          <ListItem disableGutters sx={{ py: 0 }}>
                            <ListItemText
                              primary={t("pages.smartRevision.moreConcepts", { count: chunk.length - 3 })}
                              slotProps={{
                                primary: {
                                  variant: "caption",
                                  sx: { color: "primary.main", fontWeight: 600, ml: 3.5 },
                                },
                              }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )),
          )}
        </Box>
      </Container>
    </Box>
  );
};
